import * as React from 'react';
import * as ReactGA from 'react-ga';
import { toastr } from 'react-redux-toastr';
import { Row, Col, Button, Label } from 'reactstrap';
import * as _ from 'lodash';
import { useHistory } from 'react-router';
import { match } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IReduxState } from 'src/reduxTypes';
import { initialManager, IManager, ManagersTypes, ManagerUserTypes } from 'src/share/types/manager';
import { ManagersService } from 'src/share/services/managers';
import SidebarContainer from 'src/modules/Routing/Sidebar/SidebarContainer';
import { Form } from 'src/share/components/Form/Form';
import { PulseLoader } from 'react-spinners';
import ManagerForm from './components/ManagerForm';
import routes from 'src/modules/Routing/routes';
import './manager.css';
import { RoutesModules } from 'src/modules/Routing/types';

export interface IManagerUIProps {
  match: match<{ managerId: string }>;
};

const ManagerComponent = (props: IManagerUIProps) => {
  const history = useHistory();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [loadingMessage, setLoadingMessage] = React.useState<string>('');
  const [managerInfo, setManagerInfo] = React.useState<IManager>(Object.assign({}, initialManager));
  const [beforeManagerInfo, setBeforeManagerInfo] = React.useState<IManager>(Object.assign({}, initialManager));
  const [isEditForm, setEditForm] = React.useState<boolean>(false);
  const [isAdminForm, setIsAdminForm] = React.useState<boolean>(false);
  const agencies = useSelector((state: IReduxState) => [...state.auth.agencies, ...isEditForm ? [{ code: 'ALL', name: 'Admin' }, { code: 'LEA', name: 'Law Enforcement Agent' }] : []]);
  const [userType, setUserType] = React.useState<string>('');

  const checkFormTypes = () => {
    const path = history.location.pathname;
    if (path.includes('agencies/')) {
      setIsAdminForm(false);
      setUserType(ManagersTypes.CASEMANAGER)
    } else if (path.includes('admin/')) {
      setIsAdminForm(true);
    }
    if (path.includes('edit/')) {
      setEditForm(true);
    } else {
      setEditForm(false);
    }
  }

  const getManagerUserType = (agencyCode: string) => {
    return Object.keys(ManagerUserTypes).find(managerType =>
      ManagerUserTypes[managerType] === agencyCode) || ManagersTypes.CASEMANAGER;
  }

  const checkManagerUserType = (agencyCode: string) => {
    const managerUserType = getManagerUserType(agencyCode);
    setUserType(managerUserType);
  }

  const loadManager = async () => {
    try {
      const managerId = props.match.params.managerId;
      const managerResponse = await ManagersService.getByID(managerId);
      if (_.isEmpty(managerResponse) || !managerResponse) {
        setLoading(false);
        toastr.info('The manager requested not exist in our records');
        history.replace(isAdminForm ? routes.ADMINUSERS : routes.MANAGERSVIEW);
      } else {
        setManagerInfo(managerResponse);
        if (isAdminForm) {
          checkManagerUserType(managerResponse.agencyCode);
        }
        setBeforeManagerInfo(Object.assign({}, managerResponse));
        setLoading(false);
      }
    }
    catch (error) {
      toastr.error('Error', 'There was a problem loading the manager data');
      setLoading(false);
      location.replace(routes.ADDMANAGER);
    }
  }

  React.useEffect(() => {
    checkFormTypes();
  }, [location.pathname]);

  React.useEffect(() => {
    if (isEditForm) {
      loadManager();
    } else {
      setManagerInfo(Object.assign({}, initialManager));
      setLoading(false);
    }
  }, [isEditForm, isAdminForm]);

  const handleValidSubmit = async () => {
    try {
      await save();
    } catch (error) {
      setLoading(false);
      setLoadingMessage('');
      toastr.error('Error', error.controled ? error.message : 'There was a problem connecting to the server');
    }
  }

  const save = async () => {
    if (isEditForm && _.isEqual(managerInfo, beforeManagerInfo)) {
      return toastr.info('No changes reflected in this manager update');
    }
    setLoading(true);
    setLoadingMessage('Saving Manager');
    if (isEditForm) {
      await ManagersService.update(beforeManagerInfo, managerInfo);
    } else {
      await ManagersService.save(managerInfo);
    }
    savedCorrectly();
  }

  const handleInvalidSubmit = () => {
    toastr.error('Manager', 'Please fill all the required values');
  }

  const savedCorrectly = () => {
    toastr.success('Manager', 'The manager has been saved correctly');
    setLoading(false);
    setLoadingMessage('');
    ReactGA.event({
      category: 'Manager',
      action: `${isEditForm ? 'Updated' : 'Added new'} manager`,
      label: 'Managers management'
    });
    history.replace(isAdminForm ? routes.ADMINUSERS : routes.MANAGERSVIEW);
  }

  const handleManagerInfo = (key: string, value: string) => {
    setManagerInfo(prev => ({ ...prev, [key]: value }));
  }

  const handleManagerUserType = (managerUserType: ManagersTypes) => {
    setUserType(managerUserType);
    const agencyCode = ManagerUserTypes[managerUserType];
    setManagerInfo(prev => ({ ...prev, agencyCode }));
  }

  return (
    <div className="add-manager content white-background" style={isAdminForm && userType === ManagersTypes.CASEMANAGER ? { height: '570px' } : {}}>
      <h1>{!loading ? isEditForm ? `Edit ${isAdminForm ? 'user' : 'manager'}` : `Add new ${isAdminForm ? 'user' : 'manager'}` : ''}</h1>
      <Form
        onInvalidSubmit={handleInvalidSubmit}
        onValidSubmit={handleValidSubmit}
      >
        <div className="margin-forms">
          <Row>
            <Col md="12" className="subFont">
              <ManagerForm
                managerInfo={managerInfo}
                isEditForm={isEditForm}
                loading={loading}
                isAdminForm={isAdminForm}
                onHandleManagerInfo={handleManagerInfo}
                onHandleManagerUserType={handleManagerUserType}
                agencies={agencies}
                userType={userType}
              />
              <Row>
                <Col className="col-md-7">
                  <p className="required">*Required field</p>
                </Col>
                <Col className="col-md-5">
                  <Label id="loadingText" className="text-center saving-text">{loadingMessage}</Label>
                  <PulseLoader size={15} color={'#db8733'} loading={loading} />
                  {!loading &&
                    <Button type="submit" id="submitBTN" className="btn btn-outline btn-green-fill float-right">
                      Submit
                    </Button>}
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </Form>
    </div>
  );
}

export const AgenciesManagers = SidebarContainer(RoutesModules.AGENCIES)(ManagerComponent);
export const AdminManagers = SidebarContainer(RoutesModules.ADMIN)(ManagerComponent);



// WEBPACK FOOTER //
// ./src/modules/Agencies/Manager/Add/Manager.tsx